import React, { useEffect } from 'react';
import { Button, Milestones, Sidebar } from '@insurancemenu/imenu-components-lib';
import { MilestoneStatuses } from '@insurancemenu/imenu-components-lib/dist/types';
import { useHistory } from 'react-router-dom';
import { MilestoneItemType } from '@insurancemenu/imenu-components-lib/dist/types/Milestones';
import { ButtonState, ProductTemplateChecklistOption } from 'types/productServiceV2';
import { PRODUCT_TEMPLATE_CARD_DATA } from 'global/checklists-helpers/ProductTemplateChecklistHelper';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { useRFQProductChecklistStore } from 'store/pages/rfq/rfq-template/rfq-product-checklist/pageStore';
import { createStyles } from './styles';
import {
  TEMPLATE_SIDEBAR_MILESTONE_ITEM_TEST_ID,
  TEMPLATE_SIDEBAR_MILESTONES_TEST_ID,
  TEMPLATE_SIDEBAR_REDIRECT_BTN_TEST_ID,
  TEMPLATE_SIDEBAR_TEST_ID,
} from '../constants';

const ProductTemplateSidebar = observer((): JSX.Element => {
  const classes = createStyles();
  const { templateId, pageType } = useRfqTemplate();
  const history = useHistory();

  const { productTemplate, getProductTemplateSidebarInfo, resetTemplateInfo } = useRFQProductChecklistStore();

  const convertedTemplate = toJS(productTemplate);

  useEffect(() => {
    void getProductTemplateSidebarInfo(templateId);
    return resetTemplateInfo;
  }, []);

  if (!templateId) return <></>;

  const calculateMilestoneStatus = (option: ProductTemplateChecklistOption): MilestoneStatuses => {
    switch (option.buttonState) {
      case ButtonState.VIEW_EDIT:
        return MilestoneStatuses.COMPLETED;
      case ButtonState.UNAVAILABLE:
        return MilestoneStatuses.INACTIVE;
      case ButtonState.CONTINUE:
        return MilestoneStatuses.ACTIVE;
      default:
        return MilestoneStatuses.INACTIVE;
    }
  };

  const onRedirectClick = (): void => {
    history.push('/product-templates');
  };

  const MilestonesOptions = (): MilestoneItemType[] => {
    if (convertedTemplate?.checklist) {
      const checklistOptions = convertedTemplate.checklist?.checklistOptions;
      const milestoneStatuses = checklistOptions.map((option) => ({
        label: PRODUCT_TEMPLATE_CARD_DATA[option.card].title,
        status: calculateMilestoneStatus(option),
        'data-test-id': TEMPLATE_SIDEBAR_MILESTONE_ITEM_TEST_ID,
      }));
      return milestoneStatuses;
    }
    return [];
  };

  return (
    <Sidebar className={classes.sidebar} data-test-id={TEMPLATE_SIDEBAR_TEST_ID}>
      <section className={classes.infoSection}>
        <h2 className={classes.title}>{convertedTemplate?.internalName || convertedTemplate?.name}</h2>
        {pageType === 'templates' && (
          <Button
            type="text"
            className={classes.viewProfileLink}
            size="medium"
            onClick={onRedirectClick}
            data-test-id={TEMPLATE_SIDEBAR_REDIRECT_BTN_TEST_ID}
          >
            (View UW Product Templates)
          </Button>
        )}

        <h3 className={classes.infoTitle}>External Name</h3>
        <p className={classes.infoText}>{convertedTemplate?.name}</p>

        <h3 className={classes.infoTitle}>Effective date range</h3>
        <p className={classes.infoText}>
          {`${dateFormatters.normDateToShortAmericanDate(
            convertedTemplate?.effectiveFrom || '',
          )}-${dateFormatters.normDateToShortAmericanDate(convertedTemplate?.effectiveTo || '')}`}
        </p>

        <h3 className={classes.infoTitle}>Created by</h3>
        {convertedTemplate && (
          <p className={classes.infoText}>{`${
            convertedTemplate.createdBy?.name || ''
          } on ${dateFormatters.normDateToShortAmericanDate(convertedTemplate.createdOn)}`}</p>
        )}

        {convertedTemplate?.productLineId && (
          <>
            <h3 className={classes.infoTitle}>Product line</h3>
            <p className={classes.infoText}>
              {PRODUCT_LINES_CONTENT_MAP[convertedTemplate.productLineId]?.label || ''}
            </p>
          </>
        )}
      </section>
      {convertedTemplate && (
        <section className={classes.milestones}>
          <Milestones
            milestones={MilestonesOptions()}
            allConnectingLinesWidth="4rem"
            data-test-id={TEMPLATE_SIDEBAR_MILESTONES_TEST_ID}
          />
        </section>
      )}
    </Sidebar>
  );
});

export default ProductTemplateSidebar;
